import { useState } from 'react'
import { Copy } from 'lucide-react'
import { Button } from '../../../shared/ui/button'
import { Input } from '../../../shared/ui/input'
import { BodyText, HeadingText } from '../../../shared/ui/typography'
import { ProcessorKey } from '../../../shared/assets/svg'
import { toast } from 'sonner'
import { Danger } from 'iconsax-reactjs'
import QRCode from 'react-qr-code';
import { useMutation, useQuery } from '@tanstack/react-query'
import { getTwoFactorSecretDetails, verifyMfaBackupCodeService } from '../service'
import Loader from 'src/legacy/components/loader'
import { cn } from '../../../shared/lib'
import { VerifyMfaDataType } from '../types'
import { PAGES } from '../../../shared/lib/pageUrlConstants'
import BackupCodeModal from './BackupCodeModal'
import { handleCopy, handleDownloadBackupCodes, handlePrintBackupCodes } from '../../../shared/lib/utils'
import { useLocation, useNavigate } from 'react-router'

const Setup2FA = () => {
  const [verificationCode, setVerificationCode] = useState("")
  const [openModal, setOpenModal] = useState(false)
  const [showCloseIcon, setShowCloseIcon] = useState(false)
  const { state } = useLocation()
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ["get-2fa-secret"],
    queryFn: () => getTwoFactorSecretDetails(),
    enabled: !state?.secret
  })
  const secretKey = data?.data?.secret || state?.secret;
  const qrCode = data?.data?.uri || state?.uri;
  const backupCodes = data?.data?.backupCodes || state?.backupCodes;

  const { mutateAsync: verifyTwoFactorSecretDetails, isPending: verifyTwoFactorSecretDetailsLoading } = useMutation({
    mutationFn: (data: VerifyMfaDataType) => verifyMfaBackupCodeService(data),
  })


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const data = {
      code: verificationCode,
    }
    try {
      const res = await verifyTwoFactorSecretDetails(data)
      toast.success(res?.message)
      if (!res?.error) {
        localStorage.setItem('token', res?.data?.token)
        setOpenModal(true)
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Something went wrong")
    }
  }

  return (
    <>
      <div className={cn("bg-white md:max-w-[628px] mx-auto w-11/12 rounded-[32px] p-8 md:p-[40px] shadow-[0px_4px_40px_rgba(0,0,0,0.02)] border border-primary-80", isLoading && "flex items-center justify-center")}>
        {isLoading ? <Loader /> : <div className="flex flex-col items-start gap-6">
          <div className='p-0 relative h-12 mb-2'>
            <ProcessorKey className="absolute -top-7 -left-7" />
          </div>

          <div className="space-y-1">
            <HeadingText className="text-2xl md:text-3xl font-semibold leading-tight text-gray-30">
              Set up two-factor authentication
            </HeadingText>
            <BodyText className="text-gray-10 text-sm font-[350]">
              You'll need an authenticator app (like Google or Microsoft Authenticator) to generate verification codes. Scan the QR code with your app to setup 2FA.
            </BodyText>
          </div>

          <div className="w-full flex flex-col md:flex-row gap-8 mt-4 bg-white">
            <div className="w-[180px] mx-auto md:mx-0 h-[180px] shadow-[0px_4px_40px_rgba(0,0,0,0.02)] bg-white p-1 rounded-2xl flex items-center justify-center overflow-hidden">
              <QRCode value={qrCode || ''} fgColor="#6F00FF" />,
            </div>

            <div className="flex-1 flex flex-col justify-center space-y-4">
              <div className="space-y-1 text-center md:text-left">
                <p className="text-[14px] text-[#5C638E] font-[350]">You can also copy the secret key to the app</p>
                <p className="text-[16px] font-bold text-[#161827] tracking-wider">{secretKey}</p>
              </div>

              <button
                onClick={() => handleCopy(secretKey, setShowCloseIcon)}
                className="flex items-center mx-auto md:mx-0 gap-2 text-primary-20 text-xs md:text-sm font-medium hover:opacity-80 transition-opacity w-fit"
              >
                <Copy size={16} />
                Copy
              </button>

              <div className="flex text-center md:text-left items-center gap-2 pt-1">
                <Danger size={16} className="text-[#5C638E]" />
                <p className="text-[14px] text-[#5C638E] font-[350]">Please don't share this with any other person</p>
              </div>
            </div>
          </div>

          <form className="w-full space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1">
              <span className="text-base md:text-sm font-[450] text-gray-10">Verification code</span>
              <Input
                type="text"
                placeholder="Enter 6-digit code from app"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                className="h-[56px] px-4 rounded-[8px] bg-primary-80 border-none text-[#5C638E]"
              />
            </div>

            <div className="flex items-center justify-end gap-10 pt-6">
              <a rel="noreferrer" href="mailto:admin@tradevu.com" target="_blank" className="text-[16px] font-semibold text-[#161827]">Need help?</a>
              <Button
                type="submit"
                size="lg"
                disabled={verifyTwoFactorSecretDetailsLoading || verificationCode.length !== 6 || !secretKey}
                className="!h-[48px] px-10 rounded-xl bg-[#161827] text-white hover:bg-[#20243a] disabled:bg-[#CBD0E5] disabled:text-[#F8F9FF] disabled:opacity-100"
              >
                {verifyTwoFactorSecretDetailsLoading ? "Loading..." : "Submit"}
              </Button>
            </div>
          </form>
        </div>}
      </div>
      <BackupCodeModal
        isOpen={openModal}
        onOpenChange={() => {
          setOpenModal(false)
          if (state?.secret) {
            return navigate(PAGES.accountSettings, { replace: true })
          }
          window.location.replace(PAGES.dashboard)
        }}
        backupCodes={backupCodes}
        handleCopy={() => handleCopy(backupCodes, setShowCloseIcon)}
        handleDownload={() => handleDownloadBackupCodes(backupCodes, setShowCloseIcon)}
        handlePrint={() => handlePrintBackupCodes(backupCodes, setShowCloseIcon)}
        showCloseIcon={showCloseIcon}
      />
    </>
  )
}

export default Setup2FA
